"use client";

import { useTranslations } from "use-intl";

import { CloseIcon, ExpandIcon, FilterSettingsIcon } from "@/components/appIcons";
import type { FilterOption } from "@/components/ui/filter-select";
import { FilterSelect } from "@/components/ui/filter-select";
import { FilterToolbarControl } from "@/components/ui/filter-toolbar";
import { MultiFilterSelect } from "@/components/ui/multi-filter-select";
import type { FacetDef, SurfaceConfig } from "@/facets/config";
import type { FacetFiltersController } from "@/facets/use-facet-filters";
import { cn } from "@/lib/utils";

interface FacetFilterControlsProps {
  surface: SurfaceConfig;
  options: Record<string, FilterOption[]>;
  controller: FacetFiltersController;
  showClear?: boolean;
}

type RangeValue = [number | null, number | null];

function rangeSelectValue(range: RangeValue | null): string | null {
  if (!range) return null;
  const [from, to] = range;
  if (from == null && to == null) return null;
  if (from != null && to != null) return from === to ? String(from) : `${from}-${to}`;
  if (from != null) return `${from}+`;
  return `-${to}`;
}

/** Range options are one year ("1985") or a closed span ("1980-1989"). */
function rangeOptionValue(value: string): [number, number] {
  const [from, to = from] = value.split("-");
  return [Number(from), Number(to)];
}

function renderFacetWidget({
  controller,
  def,
  label,
  options,
}: {
  controller: FacetFiltersController;
  def: FacetDef;
  label: string;
  options: FilterOption[];
}) {
  const optionValues = options.map((option) => option.value);

  if (def.widget === "multiselect") {
    const selected = controller.getValues(def.key, optionValues);
    if (options.length === 0 && selected.length === 0) return null;
    return (
      <MultiFilterSelect
        icon={def.icon}
        placeholder={label}
        options={options}
        value={selected}
        onChange={(values) => controller.setValues(def.key, values, optionValues)}
        searchable
      />
    );
  }

  if (def.widget === "range") {
    const range = controller.getRange(def.key);
    const value = rangeSelectValue(range);
    if (options.length === 0 && !value) return null;
    return (
      <FilterSelect
        icon={def.icon}
        placeholder={label}
        options={options}
        value={value}
        onChange={(next) => controller.setRange(def.key, next ? rangeOptionValue(next) : null)}
        searchable
      />
    );
  }

  if (options.length === 0) return null;
  return (
    <FilterSelect
      icon={def.icon}
      placeholder={label}
      options={options}
      value={controller.getValues(def.key, optionValues)[0] ?? null}
      onChange={(next) => controller.setValues(def.key, next ? [next] : [], optionValues)}
      searchable={def.key === "country"}
    />
  );
}

export function FacetFiltersClearButton({
  onClick,
  label,
}: {
  onClick: () => void;
  label?: string;
}) {
  const t = useTranslations("facets");

  return (
    <button
      type="button"
      onClick={onClick}
      className="col-span-2 sm:col-auto flex items-center justify-center sm:justify-start gap-1.5 h-11 sm:h-auto px-3 sm:px-2 text-sm text-muted-foreground hover:text-foreground transition-colors border border-border rounded-md sm:border-0 sm:rounded-none"
    >
      <CloseIcon className="text-[16px]" />
      {label ?? t("clear")}
    </button>
  );
}

export function MobileFacetFiltersDisclosure({
  activeCount,
  controlsId,
  open,
  onToggle,
}: {
  activeCount: number;
  controlsId: string;
  open: boolean;
  onToggle: () => void;
}) {
  const t = useTranslations("facets");

  return (
    <button
      type="button"
      aria-controls={controlsId}
      aria-expanded={open}
      onClick={onToggle}
      className="col-span-2 flex h-11 w-full items-center justify-between rounded-md border border-border bg-secondary/50 px-3 text-sm font-medium text-foreground sm:hidden"
    >
      <span className="flex items-center gap-2">
        <FilterSettingsIcon className="text-[18px]" aria-hidden="true" />
        {t("filterBy")}
        {activeCount > 0 && (
          <span className="rounded-full bg-primary px-1.5 py-0.5 text-xs font-semibold text-primary-foreground tabular-nums">
            {activeCount}
          </span>
        )}
      </span>
      <ExpandIcon
        className={cn("text-[18px] transition-transform", open && "rotate-180")}
        aria-hidden="true"
      />
    </button>
  );
}

export function FacetFilterControls({
  surface,
  options,
  controller,
  showClear = true,
}: FacetFilterControlsProps) {
  const t = useTranslations("facets");

  return (
    <>
      {surface.facets
        .filter((def) => def.key !== "format")
        .map((def) => {
          const widget = renderFacetWidget({
            controller,
            def,
            label: t(def.labelKey),
            options: options[def.key] ?? [],
          });
          if (!widget) return null;
          return (
            <FilterToolbarControl
              key={def.key}
              spanClassName={def.widget === "multiselect" ? "col-span-2 sm:col-auto" : "col-span-1"}
            >
              {widget}
            </FilterToolbarControl>
          );
        })}
      {showClear && controller.hasActive && (
        <FacetFiltersClearButton onClick={controller.clearAll} />
      )}
    </>
  );
}
